// react
import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

// hooks
import { useOrganiserActions } from './useOrganiserActions';
import { useOrganiserCareCircles } from './useOrganiserCareCircles';
import { useActionUploads } from './useActionUploads';

// store
import { getAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';
import {
  useLazyGetOrganiseDocumentQuery,
  useLinkOrganiseDocumentToEntryMutation,
} from 'store/api/organiseDocument';
import { useGetCalendarForCareCircleQuery } from 'store/api/organise';

// utils
import {
  formatDate,
  formatDateTime,
  formatRecurrence,
  combineDateTime,
  toDateInput,
  toTimeInput,
} from '@karehero/helpers/datetime';

// types
import { OrganiseEntry, OrganiseEntryStatus } from '@karehero/models';
import {
  ActionFormValues,
  isActionFormValid,
} from '@karehero/llama/pwa/pages/MemberActionsPage/ActionForm';
import {
  ActionDetailDialog,
  ActionFormDialog,
} from '@karehero/llama/pwa/pages/MemberActionsPage/MemberActionsPage';

/** Detail-dialog props the hook derives — the view owns `onClose`/`onEdit`. */
export type MemberActionDetail = Omit<ActionDetailDialog, 'onClose' | 'onEdit'>;

const emptyForm = (careCircleId: string): ActionFormValues => ({
  careCircleId,
  title: '',
  assigneeAccountId: null,
  isRecurring: false,
  recurrenceFrequency: 'weekly',
  recurrenceInterval: 1,
  date: '',
  time: '',
});

/**
 * useMemberActions wires the carer-facing actions list + detail/form dialogs for
 * a single care circle. All data shaping (entries, dialog props, handlers) lives
 * here so the view stays presentational.
 */
export const useMemberActions = (careCircleId: string) => {
  const account = useSelector(getAccount);
  const dispatch = useDispatch();
  const [getDocument] = useLazyGetOrganiseDocumentQuery();
  const [linkDocument] = useLinkOrganiseDocumentToEntryMutation();
  const uploads = useActionUploads(careCircleId);

  const { data: calendar } = useGetCalendarForCareCircleQuery(careCircleId, {
    skip: !careCircleId,
  });
  const calendarId = calendar?.id;

  // Open an attached document by fetching its (short-lived) download URL.
  const openAttachment = useCallback(
    async (documentId: string) => {
      try {
        const doc = await getDocument({ careCircleId, documentId }).unwrap();
        if (doc.downloadUrl) {
          window.open(doc.downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Couldn’t open document',
            description: 'Please try again.',
          }),
        );
      }
    },
    [getDocument, careCircleId, dispatch],
  );

  const { careCircles } = useOrganiserCareCircles();
  const careCircle = useMemo(
    () => careCircles.find((cc) => cc.id === careCircleId),
    [careCircles, careCircleId],
  );

  const activeCircles = useMemo(
    () => (careCircle ? [careCircle] : []),
    [careCircle],
  );

  const {
    isLoading,
    entries,
    calendars,
    findRawEntry,
    changeStatus,
    deleteEntry,
    createEntry,
    updateEntry,
    isPerformingAction,
  } = useOrganiserActions(activeCircles, { skip: !careCircle });

  const members = useMemo(
    () => calendars.find((c) => c.calendarId === calendarId)?.members ?? [],
    [calendars, calendarId],
  );

  // Create/edit form dialog state. null = closed.
  const [form, setForm] = useState<{
    mode: 'create' | 'edit';
    entryId?: string;
    isEditingSeries?: boolean;
    recurringEventId?: string;
    value: ActionFormValues;
  } | null>(null);

  const closeForm = useCallback(() => {
    setForm(null);
    uploads.reset();
  }, [uploads]);

  const setFormValue = useCallback(
    (patch: Partial<ActionFormValues>) =>
      setForm((f) => (f ? { ...f, value: { ...f.value, ...patch } } : f)),
    [],
  );

  const createAction = useCallback(
    () => setForm({ mode: 'create', value: emptyForm(careCircleId) }),
    [careCircleId],
  );

  const editAction = useCallback(
    (entryId: string, opts?: { editSeries?: boolean }) => {
      const entry = findRawEntry(entryId);
      if (!entry) return;

      if (opts?.editSeries && entry.recurrence) {
        setForm({
          mode: 'edit',
          entryId,
          isEditingSeries: true,
          recurringEventId: entry.recurringEventId ?? undefined,
          value: {
            careCircleId,
            title: entry.title,
            assigneeAccountId: entry.assigneeAccountId ?? null,
            isRecurring: true,
            recurrenceFrequency: entry.recurrence.freq,
            recurrenceInterval: entry.recurrence.every,
            date: toDateInput(entry.startTime),
            time: toTimeInput(entry.startTime),
          },
        });
        return;
      }

      setForm({
        mode: 'edit',
        entryId,
        value: {
          careCircleId,
          title: entry.title,
          assigneeAccountId: entry.assigneeAccountId ?? null,
          isRecurring: false,
          recurrenceFrequency: 'weekly',
          recurrenceInterval: 1,
          date: toDateInput(entry.startTime),
          time: toTimeInput(entry.startTime),
        },
      });
    },
    [findRawEntry, careCircleId],
  );

  const submitForm = useCallback(async () => {
    if (!form || !calendarId) return;
    const { value, mode } = form;
    const startTime = combineDateTime(value.date, value.time);

    if (mode === 'create') {
      const created = await createEntry({
        calendarId,
        title: value.title,
        assigneeAccountId: value.assigneeAccountId,
        startTime,
        endTime: null,
        isRecurring: value.isRecurring,
        recurrenceFrequency: value.recurrenceFrequency,
        recurrenceInterval: value.recurrenceInterval,
        isRepeatingTaskEntry: false,
        isUpdatingRepeatingTask: false,
      });

      // Link uploaded documents to the new entry. A recurring series has no
      // single entry id to link against, so skip linking in that case.
      // TODO[pwa,organise]: link attachments to recurring series occurrences
      if (
        !value.isRecurring &&
        created &&
        'id' in created &&
        uploads.documentIds.length > 0
      ) {
        await Promise.all(
          uploads.documentIds.map((documentId) =>
            linkDocument({
              careCircleId,
              documentId,
              body: { entryId: created.id, calendarId, action: 'link' },
            })
              .unwrap()
              .catch((error) => Sentry.captureException(error)),
          ),
        );
      }

      dispatch(
        newToast({
          title: 'Action created',
          description: 'Your action has been added.',
        }),
      );
    } else if (mode === 'edit' && form.entryId && form.isEditingSeries) {
      await updateEntry({
        calendarId,
        entryId: form.entryId,
        title: value.title,
        assigneeAccountId: value.assigneeAccountId,
        startTime,
        endTime: null,
        isRecurring: true,
        recurrenceFrequency: value.recurrenceFrequency,
        recurrenceInterval: value.recurrenceInterval,
        isRepeatingTaskEntry: true,
        isUpdatingRepeatingTask: true,
        recurringEventId: form.recurringEventId!,
      });

      dispatch(
        newToast({
          title: 'Action series updated',
          description: 'Your changes have been saved.',
        }),
      );
    } else if (mode === 'edit' && form.entryId) {
      await updateEntry({
        calendarId,
        entryId: form.entryId,
        title: value.title,
        assigneeAccountId: value.assigneeAccountId,
        startTime,
        endTime: null,
        isRecurring: false,
        recurrenceFrequency: value.recurrenceFrequency,
        recurrenceInterval: value.recurrenceInterval,
        isRepeatingTaskEntry: false,
        isUpdatingRepeatingTask: false,
      });

      // Link any newly uploaded documents to the entry. Existing attachments
      // stay managed from the action view.
      if (uploads.documentIds.length > 0) {
        await Promise.all(
          uploads.documentIds.map((documentId) =>
            linkDocument({
              careCircleId,
              documentId,
              body: { entryId: form.entryId!, calendarId, action: 'link' },
            })
              .unwrap()
              .catch((error) => Sentry.captureException(error)),
          ),
        );
      }

      dispatch(
        newToast({
          title: 'Action updated',
          description: 'Your changes have been saved.',
        }),
      );
    }

    closeForm();
  }, [
    form,
    calendarId,
    careCircleId,
    createEntry,
    updateEntry,
    uploads.documentIds,
    linkDocument,
    dispatch,
    closeForm,
  ]);

  // The create/edit form dialog props (null when closed). Fully built here —
  // the view passes this straight to the page.
  const formDialog = useMemo<ActionFormDialog | null>(() => {
    if (!form) return null;
    return {
      onClose: closeForm,
      form: {
        mode: form.mode,
        value: form.value,
        onChange: setFormValue,
        disableRecurrence: form.isEditingSeries,
        careCircles: careCircle
          ? [{ id: careCircle.id, name: careCircle.name }]
          : [],
        members,
        attachments: uploads.attachments,
        onUploadDocument: uploads.openFilePicker,
        onRemoveAttachment: uploads.removeUpload,
      },
      onSubmit: submitForm,
      canSubmit: isActionFormValid(form.value),
      isSubmitting: isPerformingAction || uploads.isUploading,
    };
  }, [
    form,
    closeForm,
    setFormValue,
    careCircle,
    members,
    uploads.attachments,
    uploads.openFilePicker,
    uploads.removeUpload,
    uploads.isUploading,
    submitForm,
    isPerformingAction,
  ]);

  // The read-only detail dialog props for an entry (null when not found). The
  // view supplies `onClose` (URL) and `onEdit` (recurrence routing).
  const buildDetail = useCallback(
    (entryId: string): MemberActionDetail | null => {
      const entry: OrganiseEntry | undefined = findRawEntry(entryId);
      if (!entry) return null;

      const isMine = entry.creatorAccountId === account?.id;
      const isDone = entry.status === OrganiseEntryStatus.Completed;

      return {
        title: entry.title,
        canEdit: isMine,
        isDone,
        onMarkAsDone: () =>
          changeStatus(entryId, OrganiseEntryStatus.Completed),
        onDelete: () => deleteEntry(entry.calendarId, entryId),
        detail: {
          regardingLabel: careCircle
            ? `Regarding ${careCircle.name}`
            : undefined,
          createdByLabel: entry.createdByName
            ? `Created by ${entry.createdByName}`
            : undefined,
          assigneeLabel:
            entry.assigneeAccountId === account?.id
              ? 'Assigned to you'
              : (() => {
                  const m = members.find(
                    (m) => m.id === entry.assigneeAccountId,
                  );
                  return m?.name ? `Assigned to ${m.name}` : undefined;
                })(),
          dueLabel: entry.startTime
            ? `Due ${formatDateTime(entry.startTime)}`
            : undefined,
          recurrenceLabel: entry.recurrence
            ? formatRecurrence(entry.recurrence.freq, entry.recurrence.every)
            : undefined,
          note: entry.note ?? undefined,
          linkedCall: entry.call
            ? {
                detail: formatDateTime(entry.call.scheduledAt),
                fromName: entry.createdByName ?? undefined,
              }
            : undefined,
          cta: undefined,
          attachments: (entry.attachments ?? []).map((a) => ({
            id: a.documentId,
            name: a.filename,
          })),
          done: isDone
            ? {
                dateLabel: entry.completedAt
                  ? formatDate(entry.completedAt)
                  : '',
                onMarkAsTodo: () =>
                  changeStatus(entryId, OrganiseEntryStatus.Open),
              }
            : undefined,
          onOpenAttachment: openAttachment,
          onLinkedCallClick: () => {},
        },
      };
    },
    [
      findRawEntry,
      account,
      members,
      careCircle,
      changeStatus,
      deleteEntry,
      openAttachment,
    ],
  );

  return {
    isLoading,
    careCircleName: careCircle?.name,
    entries,
    changeStatus,
    createAction,
    editAction,
    buildDetail,
    formDialog,
  };
};
